import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Viewer } from "@/features/auth/graphql/generated";
import {
  update,
  show,
  hide,
  shutdown,
  Intercom,
} from "@intercom/messenger-js-sdk";
import { useViewer } from "@/features/workspace/hooks";
import { find, some } from "lodash";

// Intercom configuration interface
export interface IntercomConfig {
  app_id: string;
  user_id?: string;
  email?: string;
  name?: string;
  user_hash?: string;
  horizontal_padding?: number;
  vertical_padding?: number;
  custom_attributes?: Record<string, any>;
  [key: string]: any; // Allow additional properties
}

// API payload interface for initialization
export interface IntercomInitPayload {
  action: "initialize";
  user_id: string;
  email: string;
  name?: string;
  custom_attributes?: Record<string, any>;
}

// API payload interface for updates
export interface IntercomUpdatePayload {
  action: "update";
  user_id: string;
  email?: string;
  name?: string;
  custom_attributes?: Record<string, any>;
}

// Hook return interface
interface UseIntercomReturn {
  initializeIntercom: (viewer: Viewer) => Promise<void>;
  updateIntercom: (data: Partial<IntercomUpdatePayload>) => Promise<void>;
  showIntercom: () => void;
  hideIntercom: () => void;
  shutdownIntercom: () => void;
  loading: boolean;
  error: string | null;
  clearError: () => void;
}

const useIntercom = (): UseIntercomReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  const { viewer } = useViewer();

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const callIntercomApi = useCallback(
    async (
      payload: IntercomInitPayload | IntercomUpdatePayload
    ): Promise<IntercomConfig> => {
      const BASE_URL = import.meta.env.VITE_SITE_URL;
      const INTERCOM_URL = `${BASE_URL}/api/intercom`;

      if (!BASE_URL) {
        throw new Error(t("intercom.errors.no_base_url"));
      }

      const response = await fetch(INTERCOM_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          errorText ||
            t("intercom.errors.api_failed", { status: response.status })
        );
      }

      const result: IntercomConfig = await response.json();

      return result;
    },
    [t]
  );

  const initializeIntercom = useCallback(
    async (viewer: Viewer) => {
      try {
        setLoading(true);
        setError(null);

        if (!viewer.id || !viewer.email) {
          throw new Error(t("intercom.errors.missing_user_data"));
        }

        // Call the API to get Intercom configuration
        const config = await callIntercomApi({
          action: "initialize",
          user_id: viewer.id?.toString() || "",
          email: viewer.email || "",
          name: viewer.name || undefined,
          custom_attributes: {
            first_name: viewer.first_name,
            last_name: viewer.last_name,
            occupation: viewer.occupation,
            phone: viewer.phone,
            avatar_url: viewer.avatar_url,
            profile_complete: !some(
              viewer.survey_completion,
              (survey) => !survey?.completed
            ),
            onboarding_type: viewer.onboarding?.type,
          },
        });
        // User ID will be available through the useViewer hook

        // Initialize Intercom with the received configuration
        try {
          Intercom(config);
        } catch (err) {
          console.error("Error initializing Intercom:", err);
          throw new Error(t("intercom.errors.intercom_not_loaded"));
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : t("intercom.errors.initialization_failed");
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [callIntercomApi, t]
  );

  const updateIntercom = useCallback(
    async (data: Partial<IntercomConfig>) => {
      try {
        setLoading(true);
        setError(null);

        if (typeof window === "undefined") {
          throw new Error(t("intercom.errors.no_window"));
        }

        if (!viewer?.id) {
          throw new Error(t("intercom.errors.no_user_id"));
        }

        const updateData = {
          user_id: viewer.id.toString(),
          email: viewer.email || "",
          name: viewer.name || "",
          custom_attributes: {
            profile_complete: !some(
              viewer.survey_completion,
              (survey) => !survey?.completed
            ),
            workspace_role: find(viewer?.person?.roles, { type: "account" })
              ?.role,
            organization_role: find(viewer?.person?.roles, {
              type: "organization",
            })?.role,
            ...data.custom_attributes,
          },
        };

        // Call the API to get updated Intercom configuration
        const config = await callIntercomApi({
          action: "update",
          ...updateData,
        });

        // Update Intercom with the received configuration
        try {
          update(config);
        } catch (err) {
          throw new Error(t("intercom.errors.intercom_not_loaded"));
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : t("intercom.errors.update_failed");
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [callIntercomApi, t, viewer]
  );

  const showIntercom = useCallback(() => {
    try {
      show();
    } catch (err) {
      console.error("Error showing Intercom:", err);
    }
  }, []);

  const hideIntercom = useCallback(() => {
    try {
      hide();
    } catch (err) {
      console.error("Error hiding Intercom:", err);
    }
  }, []);

  const shutdownIntercom = useCallback(() => {
    try {
      shutdown();
    } catch (err) {
      console.error("Error shutting down Intercom:", err);
    }
  }, []);

  return {
    initializeIntercom,
    updateIntercom,
    showIntercom,
    hideIntercom,
    shutdownIntercom,
    loading,
    error,
    clearError,
  };
};

export default useIntercom;
